import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { GetFlgFromID, APIGetSites, APIAddUpdateSites, UpdateFLG } from '../../Api'

// Async thunk to load customer data from FLG ID
export const loadCustomerData = createAsyncThunk(
    'customer/loadFromFLG',
    async (flgId, { rejectWithValue }) => {
        try {
            const [flgRecord, sites] = await Promise.all([
                GetFlgFromID(flgId),
                APIGetSites(flgId)
            ])
            return { flgRecord, sites: sites || [] }
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to load customer data')
        }
    }
)

// Async thunk to save customer data
export const saveCustomerData = createAsyncThunk(
    'customer/save',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState().customer
            await UpdateFLG(state.flg_id, state.flg_rec)
            return true
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to save customer data')
        }
    }
)

// Async thunk to save site data
export const saveSiteData = createAsyncThunk(
    'customer/saveSite',
    async (siteData, { getState, rejectWithValue }) => {
        try {
            const flgId = getState().customer.flg_id
            await APIAddUpdateSites(flgId, siteData)
            return siteData
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to save site data')
        }
    }
)

const initialState = {
    flg_id: '',
    flg_rec: {},
    name: '',
    first_name: '',
    email: '',
    full_address: '',
    postcode: '',
    rep_name: '',
    rep_email: '',
    assigned_rep: '',
    sites: [],
    selectedSite: 0,
    // Secondary customer info
    customer2_name: '',
    customer2_surname: '',
    customer2_title: '',
    customer2_email: '',
    // Property info
    conservation_area: false,
    listed_property: false,
    property_type: '',
    mpan_number: '',
    // Order info
    orderref: 100,
    extra_order_info: '',
    customer_message_rep: '',
    // Flags
    sweep_sale: false,
    selfgen: false,
    // Loading state
    loading: false,
    saving: false,
    error: null
}

const customerSlice = createSlice({
    name: 'customer',
    initialState,
    reducers: {
        setFlgId: (state, action) => {
            state.flg_id = action.payload
        },
        setFlgRecord: (state, action) => {
            state.flg_rec = action.payload
            // Extract common fields from FLG record
            if (action.payload) {
                state.name = action.payload.surname || ''
                state.first_name = action.payload.first_name || ''
                state.email = action.payload.email || ''
                state.full_address = action.payload.full_address || ''
                state.postcode = action.payload.postcode || ''
            }
        },
        setName: (state, action) => {
            state.name = action.payload
        },
        setFirstName: (state, action) => {
            state.first_name = action.payload
        },
        setEmail: (state, action) => {
            state.email = action.payload
        },
        setFullAddress: (state, action) => {
            state.full_address = action.payload
        },
        setPostcode: (state, action) => {
            state.postcode = action.payload
        },
        setRepName: (state, action) => {
            state.rep_name = action.payload
        },
        setRepEmail: (state, action) => {
            state.rep_email = action.payload
        },
        setAssignedRep: (state, action) => {
            state.assigned_rep = action.payload
        },
        setSites: (state, action) => {
            state.sites = action.payload
        },
        setSelectedSite: (state, action) => {
            state.selectedSite = action.payload
        },
        addSite: (state, action) => {
            state.sites.push(action.payload)
        },
        updateSite: (state, action) => {
            const { siteId, data } = action.payload
            const index = state.sites.findIndex(s => s.site_id === siteId)
            if (index >= 0) {
                state.sites[index] = { ...state.sites[index], ...data }
            }
        },
        deleteSite: (state, action) => {
            state.sites = state.sites.filter(s => s.site_id !== action.payload)
        },
        setCustomer2Name: (state, action) => {
            state.customer2_name = action.payload
        },
        setCustomer2Surname: (state, action) => {
            state.customer2_surname = action.payload
        },
        setCustomer2Title: (state, action) => {
            state.customer2_title = action.payload
        },
        setCustomer2Email: (state, action) => {
            state.customer2_email = action.payload
        },
        setConservationArea: (state, action) => {
            state.conservation_area = action.payload
        },
        setListedProperty: (state, action) => {
            state.listed_property = action.payload
        },
        setPropertyType: (state, action) => {
            state.property_type = action.payload
        },
        setMpanNumber: (state, action) => {
            state.mpan_number = action.payload
        },
        setOrderRef: (state, action) => {
            state.orderref = action.payload
        },
        setExtraOrderInfo: (state, action) => {
            state.extra_order_info = action.payload
        },
        setCustomerMessageRep: (state, action) => {
            state.customer_message_rep = action.payload
        },
        setSweepSale: (state, action) => {
            state.sweep_sale = action.payload
        },
        setSelfGen: (state, action) => {
            state.selfgen = action.payload
        },
        clearError: (state) => {
            state.error = null
        },
        resetCustomer: () => initialState
    },
    extraReducers: (builder) => {
        builder
            // Load customer data
            .addCase(loadCustomerData.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(loadCustomerData.fulfilled, (state, action) => {
                state.loading = false
                state.flg_rec = action.payload.flgRecord || {}
                state.sites = action.payload.sites || []
                // Extract fields from FLG record
                const flg = action.payload.flgRecord || {}
                state.name = flg.surname || ''
                state.first_name = flg.first_name || ''
                state.email = flg.email || ''
                state.full_address = flg.full_address || ''
                state.postcode = flg.postcode || ''
            })
            .addCase(loadCustomerData.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Save customer data
            .addCase(saveCustomerData.pending, (state) => {
                state.saving = true
            })
            .addCase(saveCustomerData.fulfilled, (state) => {
                state.saving = false
            })
            .addCase(saveCustomerData.rejected, (state, action) => {
                state.saving = false
                state.error = action.payload
            })
            // Save site data
            .addCase(saveSiteData.pending, (state) => {
                state.saving = true
            })
            .addCase(saveSiteData.fulfilled, (state) => {
                state.saving = false
            })
            .addCase(saveSiteData.rejected, (state, action) => {
                state.saving = false
                state.error = action.payload
            })
    }
})

export const {
    setFlgId,
    setFlgRecord,
    setName,
    setFirstName,
    setEmail,
    setFullAddress,
    setPostcode,
    setRepName,
    setRepEmail,
    setAssignedRep,
    setSites,
    setSelectedSite,
    addSite,
    updateSite,
    deleteSite,
    setCustomer2Name,
    setCustomer2Surname,
    setCustomer2Title,
    setCustomer2Email,
    setConservationArea,
    setListedProperty,
    setPropertyType,
    setMpanNumber,
    setOrderRef,
    setExtraOrderInfo,
    setCustomerMessageRep,
    setSweepSale,
    setSelfGen,
    clearError,
    resetCustomer
} = customerSlice.actions

// Selectors
export const selectFlgId = (state) => state.customer.flg_id
export const selectFlgRecord = (state) => state.customer.flg_rec
export const selectCustomerName = (state) => state.customer.name
export const selectCustomerFirstName = (state) => state.customer.first_name
export const selectCustomerEmail = (state) => state.customer.email
export const selectFullAddress = (state) => state.customer.full_address
export const selectPostcode = (state) => state.customer.postcode
export const selectRepName = (state) => state.customer.rep_name
export const selectRepEmail = (state) => state.customer.rep_email
export const selectSites = (state) => state.customer.sites
export const selectSelectedSite = (state) => state.customer.selectedSite
export const selectCurrentSite = (state) => state.customer.sites[state.customer.selectedSite]
export const selectCustomerLoading = (state) => state.customer.loading
export const selectCustomerSaving = (state) => state.customer.saving
export const selectCustomerError = (state) => state.customer.error
export const selectSweepSale = (state) => state.customer.sweep_sale
export const selectAssignedRep = (state) => state.customer.assigned_rep
export const selectExtraOrderInfo = (state) => state.customer.extra_order_info
export const selectSelfGen = (state) => state.customer.selfgen
export const selectCustomerMessageRep = (state) => state.customer.customer_message_rep

export default customerSlice.reducer
